// The scroll keep of a host that scrolls (R1761, ui-map-interface.md §16.8).
//
// The overlay writes a panel's markup again when it changed, on its 4 Hz
// pass. A host that scrolls must keep its offset across that rewrite. A
// browser keeps it on its own most of the time, but Mobile Safari on iOS 26
// threw the open queue strip back to the top at each rewrite of the
// overlay's pass, and the keep held it.
//
// The keep reads the offset before the write and again after it, and it
// writes the offset back ONLY when the rewrite moved it. A write of an
// offset that did not move is a scroll the page asks for, and on a
// telephone it would stop the glide of a list a finger has just flicked.

/** The one field of a host the keep reads and writes. */
interface Scroller { scrollTop: number }

/**
 * Runs `write`, the rewrite of `host`, and keeps the scroll offset of the
 * host across it. The read after the write lays the host out at once, which
 * is the moment its new content is measured; a browser that moved the
 * offset then gets it back. The browser clamps an offset that the new
 * content cannot hold, so a list that became shorter still ends at its foot.
 */
export function keepScroll(host: Scroller, write: () => void): void {
  const top = host.scrollTop;
  write();
  if (host.scrollTop !== top) host.scrollTop = top;
}

/**
 * Runs `write` and keeps the offset of a scroller INSIDE the host that the
 * write replaces: the card `.ck` of a context slot host, which scrolls in
 * `--ctx-maxh`. The new scroller starts at the top, so a card whose live
 * figures change each second threw the finger back to its head. `find`
 * reads the scroller before and after the write; the keep writes the old
 * offset into the new scroller only when it was off the top.
 */
export function keepInnerScroll(find: () => Scroller | null | undefined, write: () => void): void {
  const top = find()?.scrollTop ?? 0;
  write();
  if (top <= 0) return;
  const el = find();
  if (el && el.scrollTop !== top) el.scrollTop = top;
}

/** True while a popover inside `host` is open: the list of a select box
 *  (choice.ts `selectBox`). A rewrite of the host would close it. A DOM
 *  without the `:popover-open` selector (jsdom) answers false. */
export function popoverOpenIn(host: ParentNode): boolean {
  try {
    return host.querySelector(':popover-open') !== null;
  } catch {
    return false;
  }
}
